<script setup>
import {
  defineEmits,
  ref,
  reactive,
  watch,
  defineProps,
  unref,
  toRefs,
  computed,
  toValue,
} from "vue";
import CloseIcon from "@/components/svg-icons/CloseIcon.vue";
import { convertInternshipFilterCriteria } from "@/components/program-manager/sessions/utils";
import { Dialog, DialogPanel } from "@headlessui/vue";
import Pagination from "@/components/shared/Pagination.vue";
import CheckboxSelect from "@/components/shared/select/CheckboxSelect.vue";
import SearchableSelect from "@/components/shared/select/SearchableSelect.vue";
import VSelectCaret from "@/components/shared/select/VSelectCaret.vue";
import Magnifier from "@/components/shared/icons/Magnifier";
import BaseButton from "@/components/shared/Button/BaseButton.vue";
import Spinner from "@/components/helpers/Spinner.vue";
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useVueTable,
  FlexRender,
} from "@tanstack/vue-table";
import {
  useGetCityOptions,
  useGetInternships,
  useGetWorkLocations,
} from "@/components/program-manager/sessions/composable";
import { cloneDeep, debounce } from "lodash";
import { columnsConfig, internshipDurationOptions } from "./constants";
import { PRICED_SECTIONS_EXCLUDE_FILTERS } from "@/components/program-manager/sessions/constants";
import BadgeBar from "@/components/program-manager/sessions/components/BadgeBar";
import { useStore } from "vuex";
import { helpers, required } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import ErrorMessage from "@/components/shared/ErrorMessage.vue";

const props = defineProps({
  terms: {
    type: Array,
    required: false,
    default: () => [],
  },
  open: {
    type: Boolean,
    required: false,
    default: false,
  },
  originalFilters: {
    type: Object,
    required: false,
    default: () => ({
      cities: [],
      durations: undefined,
    }),
  },
  academicStartDate: {
    type: String,
    required: false,
    default: null,
  },
  internshipDuration: {
    type: Number,
    default: null,
  },
  placementTypesOptions: {
    type: Object,
    default: () => [],
  },
  internshipsOwnerId: {
    type: String,
    required: false,
  },
});

const emit = defineEmits([
  "closeModal",
  "openModal",
  "updateFilters",
  "updateSelectedInternships",
]);

const store = useStore();
const getTermsData = computed(
  () => store.getters["programManager/getTermsData"]
);
const {
  open: isModalOpen,
  terms,
  originalFilters,
  academicStartDate,
  placementTypesOptions,
} = toRefs(props);

const openModal = () => {
  isModalOpen.value = true;
  fetchInternshipsData(0, { ...unref(internshipParams), limit: 10, skip: 0 });
  emit("openModal", true);
};

const closeModal = () => {
  isModalOpen.value = false;
  emit("closeModal", true);
};

const columnHelper = createColumnHelper();
const columns = columnsConfig.map(
  ({ header, id, size, getColumnDisplayValue, type }) => {
    return columnHelper.accessor(getColumnDisplayValue, {
      id,
      header,
      size,
      type,
    });
  }
);

const filters = reactive({
  cities: cloneDeep(originalFilters.value?.cities || []),
  work_locations: cloneDeep(originalFilters?.value?.work_locations),
  durations: cloneDeep(originalFilters.value?.durations),
  terms: originalFilters.value?.terms
    ? cloneDeep(originalFilters.value?.terms)
    : cloneDeep(unref(terms)),
  placement_type: cloneDeep(originalFilters?.value?.placement_type),
});

watch(
  () => props.originalFilters,
  (updatedFilters) => {
    if (updatedFilters) {
      filters.cities = cloneDeep(updatedFilters?.cities);
      filters.durations = cloneDeep(updatedFilters?.durations);
      filters.terms = cloneDeep(updatedFilters?.terms);
      filters.work_locations = cloneDeep(updatedFilters?.work_locations);
      filters.placement_type = cloneDeep(updatedFilters?.placement_type);
    } else {
      resetFilters();
    }
  }
);

const pagination = reactive({
  pageIndex: 1,
  pageSize: 10,
});

const internshipParams = ref(convertInternshipFilterCriteria({ ...filters }));

const selectedInternships = ref([]);

const internshipIsSelected = (internshipId) => {
  return (
    selectedInternships.value.find(
      (internship) => internship?.id === internshipId
    ) !== undefined
  );
};

const onCheckboxClickedInternship = (internship) => {
  if (!internshipIsSelected(internship.id)) {
    selectedInternships.value.push(internship);
  } else {
    const index = selectedInternships.value.findIndex(
      (obj) => obj?.id === internship.id
    );
    selectedInternships.value.splice(index, 1);
  }
};

const {
  isLoading: internshipsDataLoading,
  execute: fetchInternshipsData,
  state: internshipsData,
  isReady: internshipsIsReady,
} = useGetInternships(
  { immediate: true, throwError: false },
  {
    ...unref(internshipParams),
    limit: 10,
    skip: 0,
    owner_entity_id: toValue(props.internshipsOwnerId),
    all_available_internships: true,
    is_approved_for_subdomain: true,
  }
);

const {
  isLoading: workLocationOptionsLoading,
  state: workLocationOptions,
  isReady: workLocationIsReady,
} = useGetWorkLocations(
  { immediate: true, throwError: false },
  {
    limit: 20,
    skip: 0,
  }
);

watch(
  [filters, () => props.internshipsOwnerId],
  ([newFilters, _]) => {
    internshipParams.value = convertInternshipFilterCriteria({
      ...newFilters,
    });
    if (isModalOpen.value) {
      if (pagination.pageIndex === 1) {
        changePage(1);
      } else {
        pagination.pageIndex = 1;
      }
    }
  },
  { deep: true }
);

const table = useVueTable({
  get data() {
    return internshipsData?.value?.data?.items || [];
  },
  columns: columns,
  columnResizeMode: "onChange",
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
  manualPagination: true,
  meta: { academicStartDate: unref(academicStartDate) },
});

const totalRecords = computed(() =>
  internshipsData?.value?.data?.count &&
  internshipsData?.value?.data?.count !== -1
    ? internshipsData.value.data.count
    : 0
);

watch(
  () => props.open,
  (newValue) => {
    if (newValue === true) {
      openModal();
    }
  }
);

const changePage = (pageIndex) => {
  pagination.pageIndex = pageIndex;
  fetchInternshipsData(0, {
    ...unref(internshipParams),
    limit: pagination.pageSize,
    skip: pagination.pageSize * (pagination.pageIndex - 1),
    owner_entity_id: toValue(props.internshipsOwnerId),
    all_available_internships: true,
    is_approved_for_subdomain: true,
    cancel: true,
  });
};

const {
  execute: executeFetchCityOptions,
  isLoading: cityLoading,
  state: cityOptions,
  isReady: cityIsReady,
} = useGetCityOptions(
  { immediate: true, throwError: true },
  { city_ascii: "chi" },
  true
);

const resetFilters = () => {
  filters.terms = props?.terms ? cloneDeep(props.terms) : [];
  filters.cities = [];
  filters.work_locations = undefined;
  filters.durations = undefined;
  filters.placement_type = undefined;
};

const onCancel = () => {
  isModalOpen.value = false;
  emit("closeModal", true);
};

// TODO: when this is ready, uncomment and double check all works as expected
// const addSelected = async () => {
//   // Set new emit here
//   v$.value.$reset();
//   const isValid = await v$.value.$validate();
//   if (isValid) {
//     isModalOpen.value = false;
//     emit("closeModal", true);
//     emit(
//       "updateSelectedInternships",
//       selectedInternships.value.map((internship) => ({
//         internship_id: internship.id,
//       }))
//     );
//   }
// };

const addCriteria = async () => {
  v$.value.$reset();
  const isValid = await v$.value.$validate();
  if (isValid) {
    isModalOpen.value = false;
    emit("closeModal", true);
    emit(
      "updateFilters",
      cloneDeep({ ...unref(filters), default_internship_id: undefined })
    );
  }
};

// const cityOptions = ref([]);
const fetchCityOptions = debounce(async (search, loading) => {
  if (search && search?.length > 2) {
    loading(true);
    try {
      await executeFetchCityOptions(0, { city_ascii: search, cancel: true });
      loading(false);
    } catch (e) {
      if (e?.message !== "cancel") {
        loading(false);
      }
    }
  }
}, 500);

const filterLabelMap = {
  cities: (val) => val?.city_ascii ?? val,
};

const rules = {
  terms: {
    minlength: helpers.withMessage("Choose at least one term", required),
  },
  cities: {
    minlength: helpers.withMessage("Choose at least one city", required),
  },
};

const v$ = useVuelidate(rules, filters);
</script>

<template>
  <div>
    <Dialog
      :open="isModalOpen"
      class="relative z-50"
      @close-modal="closeModal"
      @click.self="closeModal"
    >
      <div class="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel>
          <div
            class="fixed inset-0 z-0 hidden bg-university-primary opacity-50 sm:block"
          ></div>
          <div class="bg-white h-[95vh] p-6 rounded-xl z-20 relative w-[95vw]">
            <div class="text-right">
              <button @click="closeModal">
                <CloseIcon size="28" stroke="#007f80" stroke-width="1.75" />
              </button>
            </div>
            <section class="flex flex-col h-[85vh]">
              <h1
                class="flex justify-center text-xl leading-tight text-center sm:text-2xl md:text-3xl mb-6 flex-0"
              >
                Add Internships
              </h1>
              <div
                class="flex bg-blue-450 rounded-md ml-2 px-2 flex-wrap flex-shrink"
              >
                <CheckboxSelect
                  data-testid="cities_vselect"
                  v-model="filters.cities"
                  :clear-search-on-blur="() => false"
                  :close-on-select="false"
                  :components="{ OpenIndicator: Magnifier }"
                  :deselect-from-dropdown="true"
                  :filterable="false"
                  label="city_ascii"
                  :multiple="true"
                  :options="cityOptions?.items || []"
                  :loading="cityLoading || !cityIsReady"
                  outer-classes="w-52 mx-2 my-2 checkbox-placeholder"
                  placeholder="City"
                  scroll-input="auto"
                  input-max-height="29px"
                  @search="fetchCityOptions"
                >
                  <template
                    #customLabel="{ city_ascii, country, admin_name_ascii }"
                  >
                    <div class="text-sm">
                      <div class="font-semibold">{{ city_ascii }}</div>
                      <div>
                        {{ country?.name }}
                        <span v-if="country?.name && admin_name_ascii">-</span>
                        {{ admin_name_ascii }}
                      </div>
                    </div>
                  </template>
                </CheckboxSelect>
                <div class="w-52 mx-2 my-2 checkbox-placeholder">
                  <SearchableSelect
                    v-model="filters.work_locations"
                    :close-on-select="true"
                    :deselect-from-dropdown="true"
                    :filterable="false"
                    :multiple="false"
                    :searchable="false"
                    :options="workLocationOptions?.data || []"
                    :loading="
                      workLocationOptionsLoading || !workLocationIsReady
                    "
                    placeholder="Work Location"
                    label="value"
                  >
                  </SearchableSelect>
                </div>
                <VSelectCaret
                  v-model="filters.durations"
                  outer-classes="w-52 mx-2 my-2 checkbox-placeholder"
                  :options="internshipDurationOptions"
                  placeholder="Duration"
                  label="label"
                  scroll-input="auto"
                  input-max-height="29px"
                >
                </VSelectCaret>
                <CheckboxSelect
                  v-model="filters.terms"
                  :clear-search-on-blur="() => false"
                  :close-on-select="false"
                  :components="{ OpenIndicator: Magnifier }"
                  :deselect-from-dropdown="true"
                  label="name"
                  :multiple="true"
                  :options="getTermsData"
                  outer-classes="w-52 mx-2 my-2 checkbox-placeholder"
                  placeholder="Term"
                  scroll-input="auto"
                  input-max-height="29px"
                />

                <VSelectCaret
                  v-model="filters.placement_type"
                  outer-classes="w-52 mx-2 my-2 checkbox-placeholder"
                  :options="placementTypesOptions"
                  placeholder="Internship Type"
                  label="value"
                  scroll-input="auto"
                  input-max-height="29px"
                >
                </VSelectCaret>
              </div>
              <div class="grid grid-cols-12 flex-0">
                <BadgeBar
                  v-model="filters"
                  data-testid="internship-filter-criteria-badgebar"
                  :filter-label-map="filterLabelMap"
                  :exclude-filters="PRICED_SECTIONS_EXCLUDE_FILTERS"
                  class="col-span-11"
                />
                <div
                  data-testid="filters-reset-button"
                  class="ml-auto uppercase text-success-900 text-xs font-semibold mt-2 cursor-pointer"
                  @click="resetFilters"
                >
                  Reset Filters
                </div>
              </div>
              <div class="mt-2 mr-auto pl-4 flex-0">
                {{ totalRecords }} Internships
              </div>
              <div class="flex-1 flex overflow-y-auto">
                <table v-show="!internshipsDataLoading && internshipsIsReady">
                  <thead class="bg-white sticky top-0">
                    <tr
                      v-for="headerGroup in table.getHeaderGroups()"
                      :key="headerGroup.id"
                    >
                      <th
                        v-for="header in headerGroup.headers"
                        :key="header.id"
                        :colSpan="header.colSpan"
                        :style="{ width: `${header.getSize()}px` }"
                        class="py-4 pr-3 pl-2 text-left text-sm items-center text-gray-900"
                      >
                        <div class="flex items-center">
                          <FlexRender
                            v-if="!header.isPlaceholder"
                            :render="header.column.columnDef.header"
                            :props="header.getContext()"
                          />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-white">
                    <tr
                      v-for="row in table.getRowModel().rows"
                      :key="row.id"
                      class="hover:bg-blue-350"
                    >
                      <td
                        v-for="cell in row.getVisibleCells()"
                        :key="cell.column.id"
                        :style="{ width: `${cell.column.getSize()}px` }"
                        class="py-8 text-sm pl-2 first:text-teal-900 capitalize border-t-[1px] border-solid border-t-[#f1f2f4]"
                      >
                        <div v-if="cell.column.columnDef.type === 'checkbox'">
                          <input
                            type="checkbox"
                            class="h-4 w-4 border-gray-300 form-checkbox"
                            :checked="
                              internshipIsSelected(cell.row.original.id)
                            "
                            @input="
                              () =>
                                onCheckboxClickedInternship(cell.row.original)
                            "
                          />
                        </div>
                        <FlexRender
                          v-else
                          :render="cell.column.columnDef.cell"
                          :props="cell.getContext()"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
                <Spinner
                  v-show="internshipsDataLoading || !internshipsIsReady"
                  outer-classes="flex items-center justify-center bg-white h-full relative mx-auto"
                />
              </div>
              <Pagination
                class="mb-4 flex-0"
                :page="pagination.pageIndex"
                :total-of-records="totalRecords"
                :records-per-page="pagination.pageSize"
                @change-page="changePage"
              />
              <div class="flex align-center justify-center h-10 flex-0">
                <BaseButton outlined :on-click="onCancel" class="px-7"
                  >Cancel</BaseButton
                >
                <!-- <BaseButton
                  :on-click="addSelected"
                  >Add Selected</BaseButton
                  Uncomment when this is ready, for now we don't want this per https://apiabroad.atlassian.net/browse/SS-1423
                > -->
                <BaseButton :on-click="addCriteria"
                  >Add This Criteria</BaseButton
                >
              </div>
              <div v-if="v$.$invalid">
                <ErrorMessage
                  :error-classes="'error text-error-900 mt-2 text-center'"
                  :vuelidate-instance="v$"
                />
              </div>
            </section>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  </div>
</template>

<style scoped>
.checkbox-placeholder :deep(.vs__search) {
  font-size: 14px;
}
</style>
